import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Medal, ArrowRight } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { currentTier, nextTier, tierRingState, TIERS } from "@/lib/badges";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const fmt = (v) => String(Math.round(v * 10) / 10);

// Viewer stats cache — one /me/stats fetch per user session, shared by every
// popover on the page. Keyed by user_id so logout/login never shows stale hours.
let statsCache = { uid: null, promise: null };
const viewerStats = (uid) => {
  if (statsCache.uid !== uid || !statsCache.promise) {
    statsCache = { uid, promise: api.myStats().catch(() => null) };
  }
  return statsCache.promise;
};

// "How tiers work" popover — wraps every tier badge surface (dashboard ring,
// leaderboard rows, stories authors, collective members, public profiles).
// Click-driven so it works on touch, and safe inside <Link> rows: the wrapper
// span catches the bubbled click AFTER Radix has toggled, then preventDefault
// stops the row from navigating when the badge itself is tapped.
const TierPopover = ({ children }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const isVolunteer = !!user && user.user_type !== "organisation";

  useEffect(() => {
    if (!open || !isVolunteer) return;
    let live = true;
    viewerStats(user.user_id).then((s) => { if (live) setMe(s); });
    return () => { live = false; };
  }, [open, isVolunteer, user?.user_id]);

  const myHours = isVolunteer && me ? (me.total_hours || 0) : null;
  const myTier = myHours != null ? currentTier(myHours) : null;
  const myNext = myHours != null ? nextTier(myHours) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
      </span>
      <PopoverContent data-testid="tier-popover" align="start" sideOffset={8} className="w-80 rounded-2xl border-neutral-200 p-5 shadow-lg">
        <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">How tiers work</div>
        <p className="text-xs text-neutral-500 mt-1.5 leading-relaxed">
          Tiers celebrate lifetime approved volunteering hours — every hour a host confirms counts, forever.
        </p>
        <div className="mt-3 space-y-1">
          {TIERS.map((t) => {
            const isMine = myTier?.id === t.id;
            return (
              <div
                key={t.id}
                data-testid={`tier-popover-row-${t.id}`}
                className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm ${isMine ? "font-semibold" : ""}`}
                style={isMine ? { backgroundColor: `${t.color}14` } : undefined}
              >
                <span className="inline-flex items-center gap-2" style={{ color: t.color }}>
                  <Medal className="w-3.5 h-3.5" /> {t.label}
                  {isMine && (
                    <span data-testid="tier-popover-you-chip" className="text-[9px] uppercase tracking-wide rounded-full border px-1.5 py-px" style={{ borderColor: `${t.color}55` }}>
                      You
                    </span>
                  )}
                </span>
                <span className="text-neutral-500 text-xs">{t.threshold}+ hours</span>
              </div>
            );
          })}
        </div>
        {myHours != null && (
          <div data-testid="tier-popover-position" className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-600 leading-relaxed">
            {myNext ? (
              <>
                You have <b>{fmt(myHours)}h</b> approved
                {myTier && <> — <b style={{ color: myTier.color }}>{myTier.label}</b></>}
                . {fmt(myNext.threshold - myHours)}h to {myNext.label}.
              </>
            ) : (
              <>You have <b>{fmt(myHours)}h</b> approved — top tier. Every hour still counts.</>
            )}
          </div>
        )}
        {isVolunteer && (
          <Link
            to="/recognition?tab=badges"
            data-testid="tier-popover-badges-link"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-forest hover:underline"
          >
            See all your badges <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Small tier pill shown next to user names (dashboard greeting, leaderboard
// rows, area top contributors, public profiles). Tiers are LIFETIME approved
// hours — callers must pass all-time hours, never period-scoped ones.
// Renders nothing when no tier is reached yet. Click opens "How tiers work".
export const TierBadge = ({ hours, className = "" }) => {
  const tier = currentTier(hours || 0);
  if (!tier) return null;
  return (
    <TierPopover>
      <button
        type="button"
        data-testid={`tier-badge-${tier.id}`}
        title={`${tier.label} tier — how tiers work`}
        aria-label={`${tier.label} tier — how tiers work`}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle cursor-pointer hover:brightness-95 ${className}`}
        style={{ color: tier.color, borderColor: `${tier.color}55`, backgroundColor: `${tier.color}14` }}
      >
        <Medal className="w-3 h-3" style={{ color: tier.color }} />
        {tier.label}
      </button>
    </TierPopover>
  );
};

// Dashboard-only variant: the tier badge wrapped in a "next tier" progress
// ring (public areas keep the plain TierBadge — no clutter). Honest numbers:
// lifetime approved hours vs the tier thresholds in lib/badges.
export const TierProgressRing = ({ hours }) => {
  const s = tierRingState(hours);
  const R = 17;
  const C = 2 * Math.PI * R;
  // once started, always show at least a visible sliver of progress
  const shown = s.pct > 0 && s.pct < 6 ? 6 : s.pct;
  const iconColor = (s.tier || s.next).color;
  return (
    <TierPopover>
      <button
        type="button"
        data-testid="tier-progress-ring"
        title={`${s.label} — how tiers work`}
        aria-label={`${s.label} — how tiers work`}
        className="inline-flex items-center gap-2.5 ml-4 align-middle cursor-pointer"
      >
        <span className="relative inline-block w-11 h-11">
          <svg viewBox="0 0 44 44" className="w-11 h-11 -rotate-90">
            <circle cx="22" cy="22" r={R} fill="none" stroke="#E8E2D6" strokeWidth="3.5" />
            {shown > 0 && (
              <circle
                cx="22" cy="22" r={R} fill="none"
                stroke={s.color} strokeWidth="3.5" strokeLinecap="round"
                strokeDasharray={`${(shown / 100) * C} ${C}`}
                className="transition-[stroke-dasharray] duration-700"
              />
            )}
          </svg>
          <Medal className="w-4 h-4 absolute inset-0 m-auto" style={{ color: iconColor, opacity: s.tier ? 1 : 0.45 }} />
        </span>
        <span className="leading-tight text-left">
          <span
            data-testid="tier-progress-tier"
            className="block text-[11px] font-bold uppercase tracking-wide"
            style={{ color: iconColor }}
          >
            {s.tier ? s.tier.label : "First hours"}
          </span>
          <span data-testid="tier-progress-label" className="block text-[11px] font-normal normal-case tracking-normal text-neutral-500">
            {s.short}
          </span>
        </span>
      </button>
    </TierPopover>
  );
};
